import React, { useEffect, useRef, useState } from 'react';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  label: string;
  children: React.ReactNode;
}

/**
 * Right-hand slide-in panel.
 *
 * Closes on Escape and on a click outside, and parks the page behind it so the
 * board cannot be scrolled or clicked while it is open. Stays mounted for one
 * extra beat after `open` goes false, playing a `.closing` reverse of the
 * open animation instead of just vanishing. Unmounts on `animationend`, with
 * a fixed-timeout fallback: `[data-perf='low']` forces every animation's
 * duration down to near-zero, and a couple of browsers never fire
 * `animationend` at all for a animation that short — without the fallback,
 * the drawer stays mounted (and the body stays scroll-locked) forever.
 */
export function Drawer({ open, onClose, label, children }: DrawerProps): React.ReactElement | null {
  const closeButton = useRef<HTMLButtonElement>(null);
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }
    setClosing(true);
    const fallback = window.setTimeout(() => setMounted(false), 350);
    return () => window.clearTimeout(fallback);
  }, [open]);

  useEffect(() => {
    if (!mounted) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (open) closeButton.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted, open, onClose]);

  if (!mounted) return null;

  return (
    <div
      className={closing ? 'scrim closing' : 'scrim'}
      onClick={onClose}
      onAnimationEnd={() => {
        if (closing) setMounted(false);
      }}
      role="presentation"
    >
      <aside
        className={closing ? 'drawer closing' : 'drawer'}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onClick={(event) => event.stopPropagation()}
      >
        <button ref={closeButton} type="button" className="drawer-close" aria-label="Close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div className="drawer-body">{children}</div>
      </aside>
    </div>
  );
}
